<template>
  <!-- Link variant: tertiary underline button style -->
  <div
    v-if="link"
    ref="sortByRef"
    class="ds-relative"
    data-testid="sortby-select"
  >
    <AkButton
      link
      size="lg"
      :symbol="isOpen ? 'chevron-up' : 'chevron-down'"
      symbol-position="right"
      @click="toggleOpen"
    >
      {{ t('discovery.filters.sortby.label') }}
    </AkButton>
    <AkExpander
      :open="isOpen"
      :keep-alive="true"
      class="ds-absolute ds-top-full ds-right-0 ds-mt-2 ds-z-10"
      data-testid="quick-filter-dropdown"
    >
      <div class="sortby__options ds-p-4 ds-bg-white ds-shadow-lg ds-rounded-md ds-border ds-border-solid ds-border-neutral-500 ds-whitespace-nowrap">
        <AkRadio
          v-for="option in sortOptions"
          :key="option.value"
          :name="radioGroupName"
          :value="option.value"
          :text="option.label"
          :is-checked="currentValue === option.value"
          @change="onSortChange(option.value)"
        />
      </div>
    </AkExpander>
  </div>

  <!-- Default pill variant -->
  <QuickFilter
    v-else
    :title="t('discovery.filters.sortby.label')"
    :selected="currentValue !== defaultSortValue"
    data-testid="sortby-select"
  >
    <template
      v-if="activeLabel"
      #badgeAfter
    >
      <span class="ds-text-accent-alt">{{ activeLabel }}</span>
    </template>
    <div class="sortby__options">
      <AkRadio
        v-for="option in sortOptions"
        :key="option.value"
        :name="radioGroupName"
        :value="option.value"
        :text="option.label"
        :is-checked="currentValue === option.value"
        @change="onSortChange(option.value)"
      />
    </div>
  </QuickFilter>
</template>

<script setup lang="ts">
import { ref, computed, getCurrentInstance } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter, useRoute } from 'vue-router';
import { AkButton, AkRadio, AkExpander } from '@ankorstore/design-system';
import { QuickFilter } from '@bc/discovery/ui/filters';
import { onClickOutside } from '@vueuse/core';
import type { ProductSortBy } from '@bc/discovery/domain/catalog/types/product-search-parameters';
import { Context } from '@bc/discovery/domain/catalog/types/catalog';
import { getSortOptions, resolveSortBy } from '@bc/discovery/domain/catalog/config/config';

const props = defineProps<{ currentSort: ProductSortBy; link?: boolean; context?: Context }>();
const emit = defineEmits<{ 'update:sortBy': [value: ProductSortBy | null] }>();

const { t } = useI18n();
const router = useRouter();
const route = useRoute();
const radioGroupName = `sortby-${getCurrentInstance()?.uid}`;

const isOpen = ref(false);
const sortByRef = ref<HTMLElement | null>(null);

const toggleOpen = () => {
  isOpen.value = !isOpen.value;
};

onClickOutside(sortByRef, () => {
  isOpen.value = false;
});

const defaultSortValue = 'best_of';

// Which sorts this surface offers is a domain decision (getSortOptions); only the copy lives here. The
// brand page labels its default "Featured" because no sortBy param leaves the brand's curated `position`
// order in place, where the other surfaces call the same sentinel "Most relevant".
const labelFor = (context: Context | undefined): Record<ProductSortBy, string> => ({
  best_of: context === Context.BRAND ? t('discovery.filters.sortby.featured') : t('discovery.filters.sortby.bestOf'),
  'popularity:desc': t('discovery.filters.sortby.popularity'),
  'mostordered:desc': t('discovery.filters.sortby.popular'),
  'latest:desc': t('discovery.filters.sortby.mostRecent'),
  'trending:desc': t('discovery.filters.sortby.trending'),
  'max_discount_rate:desc': t('discovery.filters.sortby.discountRate'),
  'out_of_stock:asc,position:asc': '',
});

const sortOptions = computed((): { value: ProductSortBy; label: string }[] => {
  const labels = labelFor(props.context);

  return getSortOptions(props.context).map((value) => ({ value, label: labels[value] }));
});

// A sortBy this surface does not offer (a stale bookmark, a link shared from another catalog) must not
// show up as a selected-but-unlabelled control. catalog.vue drops it from the search the same way.
const currentValue = computed(() => resolveSortBy(props.context, props.currentSort) ?? defaultSortValue);

const activeLabel = computed(() => {
  if (currentValue.value === defaultSortValue) {
    return '';
  }
  return sortOptions.value.find((o) => o.value === currentValue.value)?.label ?? '';
});

const onSortChange = (value: ProductSortBy) => {
  const hash = new URLSearchParams(route.hash.slice(1));
  hash.delete('sortBy');

  const sortByValue = value === defaultSortValue ? null : value;

  if (sortByValue) {
    hash.set('sortBy', sortByValue);
  }

  const newRoute = { ...route, ...{ hash: Array.from(hash).length ? `#${hash}` : '' } };
  emit('update:sortBy', sortByValue);
  router.push(newRoute);
};
</script>

<style scoped lang="scss">
.sortby {
  &__options {
    @apply ds-flex ds-flex-col ds-gap-3;
  }

}
</style>
